import { useMemo } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { Landmark, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface CapitalPosition {
  id: string;
  legal_entity: string;
  company: string;
  classification: string | null;
  min_regulatory: number | null;
  liquid_cash_req: number | null;
  end_2025_balance: number | null;
  currency: string | null;
  source: string | null;
  method: string | null;
  can_spend: boolean | null;
  can_allocate: boolean | null;
  allocation_instrument: string | null;
  allocation_yield: number | null;
  jan: number | null; feb: number | null; mar: number | null;
  apr: number | null; may: number | null; jun: number | null;
  jul: number | null; aug: number | null; sep: number | null;
  oct: number | null; nov: number | null; dec_val: number | null;
  created_at: string;
}

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec_val'] as const;
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getCurrentMonthBalance(pos: CapitalPosition): number | null {
  const now = new Date();
  const monthIdx = now.getMonth(); // 0-based
  const key = MONTH_KEYS[monthIdx];
  const val = pos[key];
  if (val != null && val !== 0) return val;
  // Fall back to latest non-null month
  for (let i = monthIdx; i >= 0; i--) {
    const k = MONTH_KEYS[i];
    if (pos[k] != null && pos[k] !== 0) return pos[k];
  }
  return pos.end_2025_balance;
}

// formatCurrency provided by useCurrency context — call useCapitalFmt inside components
function useCapitalFmt() {
  const { fmt } = useCurrency();
  return (amount: number, currency = 'EUR') => fmt(amount, currency);
}

function getTrafficLight(balance: number | null, minRegulatory: number | null): { color: string; label: string; bg: string } {
  if (balance == null || minRegulatory == null || minRegulatory === 0) {
    return { color: 'text-muted-foreground', label: 'N/A', bg: 'bg-muted/30' };
  }
  const ratio = balance / minRegulatory;
  if (ratio >= 1.5) return { color: 'text-emerald-600', label: 'Adequate', bg: 'bg-emerald-50' };
  if (ratio >= 1.1) return { color: 'text-amber-600', label: 'Warning', bg: 'bg-amber-50' };
  return { color: 'text-red-600', label: 'Critical', bg: 'bg-red-50' };
}

const CHART_COLORS = ['#2563eb', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];

export default function Capital() {
  const formatCurrency = useCapitalFmt();
  const { data, loading, error } = useFetch<CapitalPosition[]>('/api/fin/capital');

  // Group by legal_entity for cards and chart
  const { entityGroups, chartData } = useMemo(() => {
    if (!data?.length) return { entityGroups: [], chartData: [] };

    const grouped = new Map<string, CapitalPosition[]>();
    for (const pos of data) {
      const key = pos.legal_entity;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(pos);
    }

    const groups = Array.from(grouped.entries()).map(([legalEntity, positions]) => {
      return {
        entityId: legalEntity,
        entityName: legalEntity,
        positions,
      };
    });

    // Build chart data: months on x-axis, one line per entity (sum of positions)
    const chart = MONTH_LABELS.map((label, idx) => {
      const point: Record<string, string | number> = { period: label };
      for (const g of groups) {
        const key = MONTH_KEYS[idx];
        const total = g.positions.reduce((sum, p) => sum + (p[key] ?? 0), 0);
        point[g.entityName] = total;
      }
      return point;
    });

    return { entityGroups: groups, chartData: chart };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold mb-2">Capital Positions</h1>
        <Card><CardContent className="py-12 text-center text-muted-foreground">{error}</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Landmark size={24} /> Capital Positions
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Regulatory capital adequacy by entity</p>
      </div>

      {/* Entity cards */}
      {entityGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {entityGroups.map(group => {
            // Aggregate: sum current balance and min_regulatory across all positions for the entity
            const totalBalance = group.positions.reduce((sum, p) => sum + (getCurrentMonthBalance(p) ?? 0), 0);
            const totalMinReg = group.positions.reduce((sum, p) => sum + (p.min_regulatory ?? 0), 0);
            const tl = getTrafficLight(totalBalance, totalMinReg);
            const bufferPct = totalMinReg > 0 ? `${(((totalBalance / totalMinReg) - 1) * 100).toFixed(1)}%` : 'N/A';
            const currency = group.positions[0]?.currency || 'EUR';

            return (
              <Card key={group.entityId} className="shadow-xs">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold">{group.entityName}</CardTitle>
                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tl.bg} ${tl.color}`}>
                      {tl.label === 'Adequate' && <CheckCircle2 size={12} />}
                      {tl.label === 'Warning' && <AlertTriangle size={12} />}
                      {tl.label === 'Critical' && <Shield size={12} />}
                      {tl.label}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase">Current Balance</div>
                      <div className="text-lg font-bold font-mono">
                        {formatCurrency(totalBalance, currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase">Min Regulatory</div>
                      <div className="text-lg font-bold font-mono text-muted-foreground">
                        {totalMinReg > 0 ? formatCurrency(totalMinReg, currency) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/50">
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase">Buffer</div>
                      <div className="text-sm font-medium">{bufferPct}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase">Positions</div>
                      <div className="text-sm font-medium">{group.positions.length}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase">End 2025</div>
                      <div className="text-sm font-medium">
                        {group.positions[0]?.end_2025_balance != null
                          ? formatCurrency(group.positions.reduce((s, p) => s + (p.end_2025_balance ?? 0), 0), currency)
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No capital positions found. Add positions via the database to see them here.
          </CardContent>
        </Card>
      )}

      {/* Monthly balance chart */}
      {chartData.length > 1 && entityGroups.length > 0 && (
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly Balance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(Number(v) / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  {entityGroups.map((g, i) => (
                    <Line
                      key={g.entityId}
                      type="monotone"
                      dataKey={g.entityName}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full positions table */}
      {data && data.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">All Positions</CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Legal Entity</TableHead>
                  <TableHead className="text-xs">Company</TableHead>
                  <TableHead className="text-xs">Classification</TableHead>
                  <TableHead className="text-xs text-right">Current Balance</TableHead>
                  <TableHead className="text-xs text-right">Min Regulatory</TableHead>
                  <TableHead className="text-xs text-right">Liquid Cash Req</TableHead>
                  <TableHead className="text-xs">Instrument</TableHead>
                  <TableHead className="text-xs text-right">Yield</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map(pos => {
                  const currentBalance = getCurrentMonthBalance(pos);
                  const tl = getTrafficLight(currentBalance, pos.min_regulatory);
                  const ccy = pos.currency || 'EUR';
                  return (
                    <TableRow key={pos.id}>
                      <TableCell className="text-sm font-medium">{pos.legal_entity}</TableCell>
                      <TableCell className="text-sm">{pos.company}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{pos.classification || '-'}</TableCell>
                      <TableCell className="text-sm text-right font-mono">
                        {currentBalance != null ? formatCurrency(currentBalance, ccy) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono text-muted-foreground">
                        {pos.min_regulatory != null ? formatCurrency(pos.min_regulatory, ccy) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono text-muted-foreground">
                        {pos.liquid_cash_req != null ? formatCurrency(pos.liquid_cash_req, ccy) : '-'}
                      </TableCell>
                      <TableCell className="text-sm">{pos.allocation_instrument || '-'}</TableCell>
                      <TableCell className="text-sm text-right font-mono">
                        {pos.allocation_yield != null ? `${pos.allocation_yield.toFixed(2)}%` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`text-[10px] ${tl.color}`}>
                          {tl.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
